<script setup>
import { ref } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import {useRestaurantsStore} from "@/stores"
import noImage from '@/assets/images/ui/no_restaurant_image_placeholder-360px.jpg'
import lodash from "underscore"

const props = defineProps({
  restaurantName: {
    type: Array,
    required: true
  },
  restaurantMarket: {
    type: String,
    required: true
  },
  gradient: {
    type: String
  },
  customRadius: {
    type: String
  }
})

onMounted(async () => {
  await updateRestaurantImages()
})

const restaurantsStore = useRestaurantsStore()
let restaurants = null
let bookedRestaurants = null
let preferredRestaurants = null;
({ restaurants, bookedRestaurants, preferredRestaurants } = storeToRefs(restaurantsStore))

const scroller = ref(null)

const restaurantPlaceArray = ref([])
const restaurantImageAttributionHtml = ref('')

// Helper function to get photo URL (no API call - just builds CloudFront URL)
const getPhotoUrl = (photoReference, maxWidth = 400, maxHeight = 400) => {
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
  
  return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=${maxWidth}&maxheight=${maxHeight}&photo_reference=${photoReference}&key=${apiKey}`
}

/**
 * Find restaurant data across all available stores
 * @param name - Restaurant name
 * @param market - Restaurant market
 * @returns Restaurant object or null
 */
const findRestaurantData = (name, market) => {
  // Try main restaurants store first
  if (restaurants.value?.length > 0) {
    const found = restaurants.value.find(restaurant =>
      (restaurant.name == name && restaurant.market == market))
    if (found) return found
  }

  // Fallback to booked restaurants (with raw structure)
  if (bookedRestaurants.value?.length > 0) {
    const found = bookedRestaurants.value.find(booking =>
      (booking.raw?.name == name && booking.raw?.market == market))
    if (found?.raw) return found.raw
  }

  // Fallback to preferred restaurants
  if (preferredRestaurants.value?.length > 0) {
    const found = preferredRestaurants.value.find(booking =>
      (booking.raw?.name == name && booking.raw?.market == market))
    if (found?.raw) return found.raw
  }

  return null
}


// Smart store watcher with length check to prevent redundant calls
watch(() => [restaurants.value, bookedRestaurants.value, preferredRestaurants.value], async (newVals, oldVals) => {
  // Only update if array lengths actually changed OR if this is the initial load (prevents redundant API calls)
  const lengthsChanged = newVals.some((newVal, i) => newVal?.length !== oldVals?.[i]?.length)
  const isInitialLoad = oldVals.some(val => !val)
  if (!lengthsChanged && !isInitialLoad) return

  await updateRestaurantImages()
})

// Extracted function for updating restaurant images
const updateRestaurantImages = async () => {
  const newArray = []
  for (let restaurantData of props.restaurantName) {
    // Handle different data structures - sometimes it's just a string name, sometimes an object
    const restaurantName = typeof restaurantData === 'string' ? restaurantData : restaurantData.name

    const theRestaurant = findRestaurantData(restaurantName, props.restaurantMarket)

    if (theRestaurant) {
      // Use uploaded images if available and preferred
      if (!theRestaurant.useGoogleImages && theRestaurant.imageIds?.length) {
        newArray.push({
          mainImageUrl: mediaUrl(theRestaurant.imageIds[0], 'thumb'),
          attribution: ''
        })
      }

      // ✅ Use cached Google Place data from MongoDB
      else if (theRestaurant.googlePlaceDetails?.photos?.length) {
        const photoRef = theRestaurant.googlePlaceDetails.photos[0].photo_reference
        const photoUrl = getPhotoUrl(photoRef, 360, 360)
        const attribution = theRestaurant.googlePlaceDetails.photos[0].html_attributions?.[0]

        newArray.push({
          mainImageUrl: photoUrl,
          attribution: attribution,
          photos: theRestaurant.googlePlaceDetails.photos.map(photo => ({
            ...photo,
            getUrl: (options = {}) => {
              const maxWidth = options.maxWidth || 360
              const maxHeight = options.maxHeight || 360
              
              return getPhotoUrl(photo.photo_reference, maxWidth, maxHeight)
            }
          }))
        })
      }

      // Fallback to placeholder if no images
      else {
        newArray.push({
          mainImageUrl: noImage,
          attribution: ''
        })
      }
    } else {
      // Fallback for restaurants not found in stores
      newArray.push({
        mainImageUrl: noImage,
        attribution: ''
      })
    }
  }
  restaurantPlaceArray.value = newArray
  restaurantImageAttributionHtml.value = restaurantPlaceArray.value?.[0]?.attribution

  if(restaurantPlaceArray.value.length >= 3) {
    if(scroller.value) {
      scroller.value.scrollLeft = scroller.value.clientWidth * 0.9
      await nextTick()
      scroller.value.style.scrollBehavior = 'smooth'
    }
  }
}

// Debounced watchers for props to prevent excessive API calls (reduced debounce time)
const debouncedUpdateImages = lodash.debounce(updateRestaurantImages, 100)

// Watchers for restaurantName and restaurantMarket (debounced to prevent virtual scroll chaos)
watch(() => props.restaurantName, debouncedUpdateImages)
watch(() => props.restaurantMarket, debouncedUpdateImages)
</script>


<template>
  <!-- No Image -->
  <div
    v-if="restaurantPlaceArray.length === 0"
    class="position-relative"
  >
    <VImg
      class="h-100 restaurant-image"
      :class="[customRadius]"
      :src="noImage"
      cover
      :lazy-src="noImage"
      :gradient="gradient"
    >
      <template #placeholder>
        <VRow
          class="fill-height ma-0"
          align="center"
          justify="center"
        >
          <VProgressCircular
            indeterminate
            color="grey-lighten-5"
          />
        </VRow>
      </template>
      <slot />
    </VImg>
  </div>
  <!-- Single Image -->
  <div
    v-else-if="restaurantPlaceArray.length == 1"
    class="position-relative"
  >
    <VImg
      class="h-100 restaurant-image"
      :class="[customRadius]"
      :src="restaurantPlaceArray?.[0].mainImageUrl || noImage"
      cover
      :lazy-src="noImage"
      :gradient="gradient"
    >
      <template #placeholder>
        <VRow
          class="fill-height ma-0"
          align="center"
          justify="center"
        >
          <VProgressCircular
            indeterminate
            color="grey-lighten-5"
          />
        </VRow>
      </template>
      <slot />
    </VImg>
  </div>
  <!-- Multiple Images -->
  <div
    v-else
    ref="scroller"
    class="fanned-images-container"
    :class="[{'four-plus': restaurantPlaceArray.length >= 4}]"
  >
    <div
      v-for="(restaurantPlace, index) in restaurantPlaceArray"
      :key="index"
      class="position-relative fanned-image"
    >
      <VImg
        :src="restaurantPlace.mainImageUrl || noImage"
        class="h-100"
        :class="[customRadius]"
        cover
        :style="{ 'z-index': index }"
        :lazy-src="noImage"
        :gradient="gradient"
      >
        <template #placeholder>
          <VRow
            class="fill-height ma-0"
            align="center"
            justify="center"
          >
            <VProgressCircular
              indeterminate
              color="grey-lighten-5"
            />
          </VRow>
        </template>
        <slot />
      </VImg>
    </div>
  </div>
</template>


<style lang="scss" scoped>
.restaurant-image-attribution {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 0.25em 0.5em 0.25em 0.75em;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 0.1rem;
  font-weight: 500;
  border-radius: 0.25rem;
  max-width: 100%;
}

.restaurant-image-attribution a {
  color: white;
}

.scrollable-images-container {
  height: 100%;
}

.fanned-images-container {
  overflow-x: auto;
  white-space: nowrap;
  height: 100%;
  cursor: grab;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.fanned-images-container::-webkit-scrollbar { /* WebKit */
  display: none;
}

.fanned-images-container:active {
  cursor: grabbing;
}

.fanned-image {
  display: inline-block;
  height: 100%;
  width: 100%;
  border-radius: 0.375rem;
  margin-left: -64%;
  max-width: 80%;
  overflow: hidden;
}

.four-plus .fanned-image {
  max-width: 70%;
  margin-left: -50%;
}

.fanned-image:first-child {
  margin-left: 0;
}

.fanned-image:last-child {
  margin-right: 12px;
}
</style>
